import { useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import type {
  RolloverAuditDetail,
  LeaseUpAuditDetail,
  AnnualCashFlow,
  ProjectionResult,
} from "../../api/client";
import { fmtCurrency, fmtPSF, fmtPercent, fmtDate, fmtSF } from "../../utils/format";
import { downloadCSV } from "../../utils/csv";

// ---------- helpers ----------

interface TenantRolloverGroup {
  tenant_id: string;
  tenant_name: string;
  events: RolloverAuditDetail[];
}

function groupByTenant(audit: RolloverAuditDetail[]): TenantRolloverGroup[] {
  const map: Record<string, TenantRolloverGroup> = {};
  for (const detail of audit) {
    let group = map[detail.tenant_id];
    if (!group) {
      group = { tenant_id: detail.tenant_id, tenant_name: detail.tenant_name, events: [] };
      map[detail.tenant_id] = group;
    }
    group.events.push(detail);
  }
  const groups = Object.values(map);
  for (const group of groups) {
    group.events.sort((a, b) => a.year - b.year);
  }
  return groups.sort((a, b) => a.tenant_name.localeCompare(b.tenant_name));
}

// ---------- RolloverEventRow ----------

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className="tabular-nums font-medium text-bark-700">{value}</span>
    </div>
  );
}

function ScenarioCard({ title, rows }: { title: string; rows: { label: string; value: string }[] }) {
  return (
    <div className="bg-white border border-grove-200 rounded-lg p-4">
      <h5 className="font-semibold text-bark-600 mb-2 uppercase tracking-wider text-[11px]">{title}</h5>
      <div className="space-y-1.5 text-bark-500">
        {rows.map((r) => <DetailRow key={r.label} label={r.label} value={r.value} />)}
      </div>
    </div>
  );
}

const tdCls = "px-4 py-3 text-sm text-bark-600 tabular-nums whitespace-nowrap";

function RolloverEventRow({ event }: { event: RolloverAuditDetail }) {
  const [expanded, setExpanded] = useState(false);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <>
      <tr className="border-b border-grove-200 last:border-0 hover:bg-grove-50 transition-colors cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <td className="px-4 py-3 text-sm text-bark-700 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            <Chevron className="w-3.5 h-3.5 text-bark-400" />
            {event.year}
            {event.is_re_expiration && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-honey-100 text-honey-700 border border-honey-200 rounded">Re-exp</span>
            )}
          </div>
        </td>
        <td className={tdCls}>{fmtDate(event.expiration_date)}</td>
        <td className={`${tdCls} text-right`}>{fmtPercent(event.renewal_probability)}</td>
        <td className={`${tdCls} text-right`}>{fmtPSF(event.expiring_rent_psf)}</td>
        <td className={`${tdCls} text-right`}>{fmtPSF(event.market_rent_psf)}</td>
        <td className={`${tdCls} text-right font-medium text-bark-700`}>{fmtPSF(event.weighted_rent_psf)}</td>
        <td className={`${tdCls} text-right`}>{event.weighted_downtime_months.toFixed(1)} mo</td>
        <td className={`${tdCls} text-right`}>{fmtCurrency(event.weighted_ti_total)}</td>
        <td className={`${tdCls} text-right`}>{fmtCurrency(event.weighted_lc_total)}</td>
        <td className={`${tdCls} text-right`}>{event.new_lease_term_years} yr</td>
        <td className={tdCls}>{fmtDate(event.new_expiration_date)}</td>
      </tr>
      {expanded && (
        <tr className="bg-grove-50/50">
          <td colSpan={11} className="px-4 py-3">
            <div className="ml-6 grid grid-cols-2 gap-6 text-xs">
              <ScenarioCard
                title={`Renewal Scenario (${fmtPercent(event.renewal_probability)} prob.)`}
                rows={[
                  { label: "Rent PSF", value: fmtPSF(event.expiring_rent_psf) },
                  { label: "Downtime", value: `${event.renewal_downtime_months} mo` },
                  { label: "Tenant Improvements", value: fmtCurrency(event.renewal_ti_total) },
                  { label: "Leasing Commissions", value: fmtCurrency(event.renewal_lc_total) },
                ]}
              />
              <ScenarioCard
                title={`New Tenant Scenario (${fmtPercent(1 - event.renewal_probability)} prob.)`}
                rows={[
                  { label: "Market Rent PSF", value: fmtPSF(event.market_rent_psf) },
                  { label: "Downtime", value: `${event.new_tenant_downtime_months} mo` },
                  { label: "Tenant Improvements", value: fmtCurrency(event.new_tenant_ti_total) },
                  { label: "Leasing Commissions", value: fmtCurrency(event.new_tenant_lc_total) },
                ]}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------- RolloverAuditSection ----------

function RolloverAuditSection({
  audit,
}: {
  audit: RolloverAuditDetail[];
  cashFlows: AnnualCashFlow[];
}) {
  const groups = useMemo(() => groupByTenant(audit), [audit]);

  if (groups.length === 0) {
    return (
      <div className="bg-white border border-grove-300 rounded-xl p-8 text-center">
        <p className="text-sm text-bark-400">
          No lease rollover events in this projection. All leases extend past the hold period.
        </p>
      </div>
    );
  }

  const totalWeightedTI = audit.reduce((s, e) => s + e.weighted_ti_total, 0);
  const totalWeightedLC = audit.reduce((s, e) => s + e.weighted_lc_total, 0);
  const reExpirations = audit.filter((e) => e.is_re_expiration).length;

  const thCls = "text-[11px] font-semibold text-bark-400 uppercase tracking-wider";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Rollover Events", value: String(audit.length) },
          { label: "Re-expirations", value: String(reExpirations) },
          { label: "Total Weighted TI", value: fmtCurrency(totalWeightedTI) },
          { label: "Total Weighted LC", value: fmtCurrency(totalWeightedLC) },
        ].map((c) => (
          <div key={c.label} className="bg-white border border-grove-300 rounded-xl p-4">
            <p className="text-xs font-medium text-bark-400 uppercase tracking-wider">{c.label}</p>
            <p className="text-2xl font-semibold text-bark-800 tabular-nums mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      {groups.map((group) => (
        <div key={group.tenant_id} className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 bg-grove-50 border-b border-grove-200">
            <h4 className="text-sm font-semibold text-bark-700">{group.tenant_name}</h4>
            <p className="text-xs text-bark-400 mt-0.5">
              {group.events.length} rollover event{group.events.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-grove-200 bg-grove-50/50">
                  <th className={`text-left px-4 py-2.5 ${thCls}`}>Year</th>
                  <th className={`text-left px-4 py-2.5 ${thCls}`}>Expiration</th>
                  <th className={`text-right px-4 py-2.5 ${thCls}`}>Renewal Prob.</th>
                  <th className={`text-right px-4 py-2.5 ${thCls}`}>Expiring Rent</th>
                  <th className={`text-right px-4 py-2.5 ${thCls}`}>Market Rent</th>
                  <th className={`text-right px-4 py-2.5 ${thCls}`}>Weighted Rent</th>
                  <th className={`text-right px-4 py-2.5 ${thCls}`}>Downtime</th>
                  <th className={`text-right px-4 py-2.5 ${thCls}`}>Weighted TI</th>
                  <th className={`text-right px-4 py-2.5 ${thCls}`}>Weighted LC</th>
                  <th className={`text-right px-4 py-2.5 ${thCls}`}>New Term</th>
                  <th className={`text-left px-4 py-2.5 ${thCls}`}>New Expiration</th>
                </tr>
              </thead>
              <tbody>
                {group.events.map((event, idx) => (
                  <RolloverEventRow key={idx} event={event} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- LeaseUpAuditSection ----------

function LeaseUpAuditSection({ audit }: { audit: LeaseUpAuditDetail[] }) {
  if (audit.length === 0) {
    return (
      <div className="bg-white border border-grove-300 rounded-xl p-8 text-center">
        <p className="text-sm text-bark-400">
          No lease-up spaces in this projection. All spaces are currently occupied.
        </p>
      </div>
    );
  }

  const totalTI = audit.reduce((s, e) => s + e.ti_total, 0);
  const totalLC = audit.reduce((s, e) => s + e.lc_total, 0);
  const totalSF = audit.reduce((s, e) => s + e.leased_sf, 0);
  const specCount = audit.filter((e) => e.space_status === "speculative").length;
  const darkCount = audit.filter((e) => e.space_status === "dark_tenant").length;

  const thCls = "text-[11px] font-semibold text-bark-400 uppercase tracking-wider";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: "Lease-Up Spaces", value: String(audit.length), color: "text-bark-800" },
          { label: "Speculative", value: String(specCount), color: "text-honey-700" },
          { label: "Dark Tenants", value: String(darkCount), color: "text-purple-700" },
          { label: "Total TI", value: fmtCurrency(totalTI), color: "text-bark-800" },
          { label: "Total LC", value: fmtCurrency(totalLC), color: "text-bark-800" },
        ].map((c) => (
          <div key={c.label} className="bg-white border border-grove-300 rounded-xl p-4">
            <p className="text-xs font-medium text-bark-400 uppercase tracking-wider">{c.label}</p>
            <p className={`text-2xl font-semibold tabular-nums mt-1 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
        <div className="px-4 py-3 bg-grove-50 border-b border-grove-200">
          <h4 className="text-sm font-semibold text-bark-700">
            Lease-Up Schedule ({fmtSF(totalSF)} total)
          </h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-grove-200 bg-grove-50/50">
                <th className={`text-left px-4 py-2.5 ${thCls}`}>Status</th>
                <th className={`text-left px-4 py-2.5 ${thCls}`}>Suite</th>
                <th className={`text-left px-4 py-2.5 ${thCls}`}>Tenant / Space</th>
                <th className={`text-right px-4 py-2.5 ${thCls}`}>SF</th>
                <th className={`text-left px-4 py-2.5 ${thCls}`}>Commencement</th>
                <th className={`text-right px-4 py-2.5 ${thCls}`}>Rent PSF</th>
                <th className={`text-right px-4 py-2.5 ${thCls}`}>Term</th>
                <th className={`text-right px-4 py-2.5 ${thCls}`}>TI</th>
                <th className={`text-right px-4 py-2.5 ${thCls}`}>LC</th>
                <th className={`text-left px-4 py-2.5 ${thCls}`}>Profile / Reimb.</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((item, idx) => (
                <tr key={idx} className="border-b border-grove-200 last:border-0 hover:bg-grove-50 transition-colors">
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    {item.space_status === "speculative" ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-honey-100 text-honey-800 border border-honey-200">Speculative</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-800 border border-purple-200">Dark Tenant</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-bark-500 whitespace-nowrap">{item.suite_id || "\u2014"}</td>
                  <td className="px-4 py-3 text-sm font-medium text-bark-700 whitespace-nowrap">{item.tenant_name}</td>
                  <td className="px-4 py-3 text-sm text-bark-600 tabular-nums text-right whitespace-nowrap">{fmtSF(item.leased_sf)}</td>
                  <td className="px-4 py-3 text-sm text-bark-600 whitespace-nowrap">
                    {item.absorption_months
                      ? `${item.absorption_months} mo absorption`
                      : item.lease_commencement_date
                        ? fmtDate(item.lease_commencement_date)
                        : "\u2014"}
                  </td>
                  <td className="px-4 py-3 text-sm text-bark-600 tabular-nums text-right whitespace-nowrap">{fmtPSF(item.rent_psf_at_commencement)}</td>
                  <td className="px-4 py-3 text-sm text-bark-600 tabular-nums text-right whitespace-nowrap">{item.lease_term_years ? `${item.lease_term_years} yr` : "\u2014"}</td>
                  <td className="px-4 py-3 text-sm text-bark-600 tabular-nums text-right whitespace-nowrap">{fmtCurrency(item.ti_total)}</td>
                  <td className="px-4 py-3 text-sm text-bark-600 tabular-nums text-right whitespace-nowrap">{fmtCurrency(item.lc_total)}</td>
                  <td className="px-4 py-3 text-sm text-bark-500 whitespace-nowrap">
                    {item.market_leasing_profile_name || item.reimbursement_method || "\u2014"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------- RolloverTab (exported) ----------

interface RolloverTabProps {
  projectionResult: ProjectionResult;
}

export default function RolloverTab({ projectionResult }: RolloverTabProps) {
  const audit = projectionResult.rollover_audit || [];
  const leaseUpAudit = projectionResult.lease_up_audit || [];
  const cashFlows = projectionResult.annual_cash_flows || [];

  function exportRolloverCSV() {
    const rows: (string | number)[][] = [];

    // Lease-up section
    if (leaseUpAudit.length > 0) {
      rows.push(["--- Lease-Up Schedule ---"]);
      rows.push(["Status", "Suite", "Tenant", "SF", "Commencement", "Rent PSF", "Term (yr)", "TI", "LC"]);
      for (const item of leaseUpAudit) {
        rows.push([
          item.space_status || "",
          item.suite_id || "",
          item.tenant_name,
          item.leased_sf,
          item.lease_commencement_date || "",
          item.rent_psf_at_commencement ?? "",
          item.lease_term_years ?? "",
          Math.round(item.ti_total),
          Math.round(item.lc_total),
        ]);
      }
      rows.push([]);
    }

    // Rollover section
    if (audit.length > 0) {
      rows.push(["--- Lease Rollover Audit ---"]);
      rows.push(["Tenant", "Year", "Expiration", "Renewal Prob.", "Expiring Rent PSF", "Market Rent PSF", "Weighted Rent PSF", "Downtime (mo)", "Weighted TI", "Weighted LC", "New Term (yr)", "New Expiration"]);
      for (const e of audit) {
        rows.push([
          e.tenant_name,
          e.year,
          e.expiration_date || "",
          fmtPercent(e.renewal_probability, 1),
          e.expiring_rent_psf?.toFixed(2) ?? "",
          e.market_rent_psf?.toFixed(2) ?? "",
          e.weighted_rent_psf?.toFixed(2) ?? "",
          e.weighted_downtime_months?.toFixed(1) ?? "",
          Math.round(e.weighted_ti_total),
          Math.round(e.weighted_lc_total),
          e.new_lease_term_years ?? "",
          e.new_expiration_date || "",
        ]);
      }
    }

    if (rows.length > 0) {
      downloadCSV(rows, "audit_rollover.csv");
    }
  }

  return (
    <div className="space-y-8">
      {/* Download button */}
      {(audit.length > 0 || leaseUpAudit.length > 0) && (
        <div className="flex justify-end print:hidden">
          <button
            onClick={exportRolloverCSV}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-bark-500 uppercase tracking-wider mb-4">
          Lease-Up Summary
        </h3>
        <LeaseUpAuditSection audit={leaseUpAudit} />
      </div>

      <div>
        <h3 className="text-sm font-semibold text-bark-500 uppercase tracking-wider mb-4">
          Lease Rollover Audit
        </h3>
        <RolloverAuditSection audit={audit} cashFlows={cashFlows} />
      </div>
    </div>
  );
}
